import { apiFetch } from "./client";

export interface M365Integration {
  id: string;
  companyId: string;
  status: "disconnected" | "consent_pending" | "connected" | "error";
  authMode: "admin_consent_app" | "oauth_user";
  oauthSignedInAs: string | null;
  oauthScopesGranted: string[] | null;
  tokenExpiresAt: string | null;
  scopes: string[] | null;
  enabled: boolean;
  metadata: Record<string, unknown> | null;
  tenantDomain: string | null;
  consentedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface M365OAuthIdentity {
  authMode: "admin_consent_app" | "oauth_user";
  signedInAs: string | null;
  scopesGranted: string[] | null;
  tokenExpiresAt: string | null;
  status: "disconnected" | "consent_pending" | "connected" | "error";
}

export interface M365ConnectResult {
  integrationId: string;
  consentUrl: string;
}

export interface GraphTeam {
  id: string;
  displayName: string;
  description: string | null;
}

export interface GraphChannel {
  id: string;
  displayName: string;
  description: string | null;
  membershipType: string;
}

export interface GraphUser {
  id: string;
  displayName: string;
  mail: string | null;
  userPrincipalName: string;
}

export interface GraphSite {
  id: string;
  displayName: string;
  webUrl: string;
  name: string;
}

export interface GraphDrive {
  id: string;
  name: string;
  driveType: string;
  webUrl: string;
}

export interface SharePointConfig {
  siteId: string;
  siteName: string;
  driveId: string;
  driveName: string;
  folderPath: string;
}

export interface PlatformReadiness {
  configured: boolean;
  hasEntraApp: boolean;
  callbackUri: string;
}

export const m365Api = {
  // --- Platform readiness ---
  checkReadiness: () =>
    apiFetch<PlatformReadiness>("/m365/readiness"),

  // --- Core integration ---
  getStatus: (companyId: string) =>
    apiFetch<M365Integration | { status: "not_configured" }>(`/m365/${companyId}`),

  connect: (companyId: string) =>
    apiFetch<M365ConnectResult>(`/m365/${companyId}/connect`, {
      method: "POST",
    }),

  disconnect: (companyId: string) =>
    apiFetch<M365Integration>(`/m365/${companyId}/disconnect`, {
      method: "POST",
    }),

  update: (companyId: string, data: { scopes?: string[]; enabled?: boolean }) =>
    apiFetch<M365Integration>(`/m365/${companyId}`, {
      method: "PATCH",
      body: JSON.stringify(data),
    }),

  // --- SharePoint config ---
  getSharePointConfig: (companyId: string) =>
    apiFetch<SharePointConfig | null>(`/m365/${companyId}/sharepoint-config`),

  updateSharePointConfig: (companyId: string, config: SharePointConfig) =>
    apiFetch<{ sharepointConfig: SharePointConfig }>(`/m365/${companyId}/sharepoint-config`, {
      method: "PUT",
      body: JSON.stringify(config),
    }),

  // --- Graph API proxy ---
  listTeams: (companyId: string) =>
    apiFetch<GraphTeam[]>(`/m365/${companyId}/graph/teams`),

  listChannels: (companyId: string, teamId: string) =>
    apiFetch<GraphChannel[]>(`/m365/${companyId}/graph/teams/${teamId}/channels`),

  listUsers: (companyId: string, query?: string) =>
    apiFetch<GraphUser[]>(`/m365/${companyId}/graph/users${query ? `?q=${encodeURIComponent(query)}` : ""}`),

  listSites: (companyId: string, query?: string) =>
    apiFetch<GraphSite[]>(`/m365/${companyId}/graph/sites${query ? `?q=${encodeURIComponent(query)}` : ""}`),

  listDrives: (companyId: string, siteId: string) =>
    apiFetch<GraphDrive[]>(`/m365/${companyId}/graph/sites/${siteId}/drives`),

  // --- Test notification ---
  testNotification: (
    companyId: string,
    data: {
      channel: "teams_channel" | "outlook";
      webhookUrl?: string;
      recipientEmail?: string;
    },
  ) =>
    apiFetch<{ success: boolean; error?: string }>(`/m365/${companyId}/test-notification`, {
      method: "POST",
      body: JSON.stringify(data),
    }),

  // --- Per-agent mailbox ---
  listMailboxes: (companyId: string) =>
    apiFetch<Array<{ id: string; displayName: string; mail: string; type: "user" | "shared" }>>(
      `/m365/${companyId}/mailboxes`,
    ),

  getMailboxSummary: (companyId: string) =>
    apiFetch<{
      totalAgents: number;
      configured: number;
      verified: number;
      pending: number;
      failed: number;
      scopeRequiredFlag: boolean;
    }>(`/m365/${companyId}/mailbox-summary`),

  getAgentMailbox: (companyId: string, agentId: string) =>
    apiFetch<AgentMailboxRow | null>(`/m365/${companyId}/agent/${agentId}/mailbox`),

  setAgentMailbox: (
    companyId: string,
    agentId: string,
    config: { mailboxUpn: string; mailboxObjectId: string; mailboxAlias: string },
  ) =>
    apiFetch<AgentMailboxRow>(`/m365/${companyId}/agent/${agentId}/mailbox`, {
      method: "POST",
      body: JSON.stringify(config),
    }),

  removeAgentMailbox: (companyId: string, agentId: string) =>
    apiFetch<{ removed: number }>(`/m365/${companyId}/agent/${agentId}/mailbox`, {
      method: "DELETE",
    }),

  getAgentMailboxSetupScript: async (companyId: string, agentId: string): Promise<string> => {
    const resp = await fetch(`/api/m365/${companyId}/agent/${agentId}/mailbox/setup-script`, {
      credentials: "include",
    });
    if (!resp.ok) {
      const body = await resp.text();
      throw new Error(body || `setup-script failed (${resp.status})`);
    }
    return resp.text();
  },

  verifyAgentMailboxScope: (companyId: string, agentId: string) =>
    apiFetch<{ ok: boolean; mailbox: AgentMailboxRow; error?: string }>(
      `/m365/${companyId}/agent/${agentId}/mailbox/verify-scope`,
      { method: "POST" },
    ),

  // --- Service-user OAuth (oauth_user mode) ---
  startOAuth: (companyId: string, loginHint?: string) =>
    apiFetch<{ integrationId: string; authorizeUrl: string; scopes: string[] }>(
      `/m365/${companyId}/oauth/start`,
      { method: "POST", body: JSON.stringify({ loginHint: loginHint ?? null }) },
    ),

  disconnectOAuth: (companyId: string) =>
    apiFetch<{ ok: boolean }>(`/m365/${companyId}/oauth/disconnect`, {
      method: "POST",
    }),

  getOAuthIdentity: (companyId: string) =>
    apiFetch<M365OAuthIdentity | null>(`/m365/${companyId}/oauth/identity`),

  // --- Teams ---
  teamsReadiness: () =>
    apiFetch<{ configured: boolean; hasBotIdentity: boolean; activationDeepLink: null }>(
      `/teams/readiness`,
    ),

  getTeamsInstallStatus: (companyId: string) =>
    apiFetch<{
      platformConfigured: boolean;
      install: {
        status: "not_installed" | "publishing" | "published" | "uninstalled" | "error";
        manifestVersion?: string;
        publishedTeamsAppId?: string | null;
        lastError?: string | null;
        publishedAt?: string | null;
      };
      activationDeepLink: string | null;
    }>(`/m365/${companyId}/teams/install-status`),

  installTeamsBot: (companyId: string) =>
    apiFetch<{ ok: boolean; teamsAppId: string; skillSync: { total: number; pushed: number } }>(
      `/m365/${companyId}/teams/install`,
      { method: "POST" },
    ),

  uninstallTeamsBot: (companyId: string) =>
    apiFetch<{ ok: boolean }>(`/m365/${companyId}/teams/uninstall`, {
      method: "POST",
    }),
};

export type M365MailboxScopeStatus =
  | "selected_pending_rbac"
  | "rbac_verified"
  | "rbac_failed";

export interface AgentMailboxRow {
  id: string;
  agentId: string;
  companyId: string;
  mailboxUpn: string;
  mailboxObjectId: string;
  mailboxAlias: string;
  scopeStatus: M365MailboxScopeStatus;
  scopeAppliedAt: string | null;
  scopeVerifiedAt: string | null;
  lastVerificationError: string | null;
  createdAt: string;
  updatedAt: string;
}
